import { GpmPayConfigError } from '../../core/errors.js';
import type { SimulatorOptions } from '../../resources/simulator.js';
import { enumArg, intArg, requireArg } from '../args.js';
import { clientFromFlags, type GlobalFlags } from '../context.js';
import { printJson } from '../redact.js';
import { EXIT, err, fields, money, out, style, symbols } from '../ui.js';

export interface SimulateFlags extends GlobalFlags {
  account?: string | undefined;
  amount?: string | undefined;
  content?: string | undefined;
  /** Silent fallback for `--content`; the help only documents `--content`. */
  desc?: string | undefined;
  type?: string | undefined;
  allowProduction?: boolean | undefined;
}

function simulatorOptions(flags: SimulateFlags): SimulatorOptions {
  if (flags.allowProduction !== true) return {};

  // stderr, so it survives `--json | jq` and is impossible to miss.
  err(
    `${symbols.warn()} ${style.yellow('--allow-production')} — writing a simulated transaction to the ` +
      'live ledger.',
  );
  err(
    style.dim(
      '  It is tagged source=SIMULATED, but it stays visible in the merchant\n' +
        '  dashboard and fires webhooks for every endpoint with fireOnSimulated on.',
    ),
  );
  return { allowOnProduction: true };
}

const AFTER_SIMULATE = [
  '  Delivery is asynchronous — give it a moment.',
  '  Webhooks fire only for endpoints with fireOnSimulated enabled:',
  '    gpmpay webhook settings',
].join('\n');

export async function simulateCommand(
  subcommand: string | undefined,
  flags: SimulateFlags,
): Promise<number> {
  const client = clientFromFlags(flags);

  switch (subcommand) {
    case 'tx': {
      const bankAccountId = requireArg(flags.account, 'account');
      const amount = intArg(
        requireArg(flags.amount, 'amount'),
        'amount',
      ) as number;
      const transferContent = requireArg(
        flags.content ?? flags.desc,
        'content',
      );
      const type = enumArg(flags.type, 'type', ['IN', 'OUT'] as const) ?? 'IN';

      const options = simulatorOptions(flags);
      const result = await client.simulator.createTransaction(
        { bankAccountId, amount, transferContent, type },
        options,
      );

      if (flags.json === true) {
        printJson(result);
        return EXIT.OK;
      }

      const { transaction, historyIds } = result;
      out(`${symbols.ok()} Simulated ${type} ${money(amount)}`);
      out(
        fields([
          ['Transaction', transaction.id],
          ['Account', bankAccountId],
          ['Content', transferContent],
          ['Source', transaction.source],
          // 0 here is the answer to "why didn't my handler fire?" — no endpoint
          // has fireOnSimulated on, so nothing was ever queued.
          ['Webhooks', `${historyIds.length} queued`],
        ]),
      );
      out();
      out(style.dim(AFTER_SIMULATE));
      return EXIT.OK;
    }

    default:
      throw new GpmPayConfigError(
        `Unknown subcommand "simulate ${subcommand ?? ''}". Expected: tx.`,
        'invalid_argument',
      );
  }
}
